/**
 * Data Loss Prevention (DLP) & Contact Information Masking Utility
 * Prevents unintentional leakage of phone numbers, emails, URLs, and payment handles.
 */

export interface DLPMaskResult {
  maskedText: string;
  detectedTypes: string[];
  detectionsCount: number;
}

// Regex patterns for Indian & International contact identifiers
const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/gi;

// Matches +91, 10-digit mobile numbers with or without dashes/spaces/parentheses
const PHONE_REGEX = /(\+?91[\s-]?)?[6-9]\d{9}|(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g;

// Matches UPI payment identifiers (e.g. name@okhdfcbank, user@paytm, user@upi)
const UPI_REGEX = /[a-zA-Z0-9.\-_]{2,256}@(okhdfcbank|okaxis|okicici|oksbi|paytm|upi|ybl|ibl|axl)/gi;

// Matches suspicious web URLs
const URL_REGEX = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/gi;

/**
 * Deterministically masks sensitive contact information from text before AI or consultation transmission
 */
export function maskSensitiveContactInfo(rawText: string): DLPMaskResult {
  if (!rawText) {
    return { maskedText: '', detectedTypes: [], detectionsCount: 0 };
  }

  const detectedTypes: Set<string> = new Set();
  let count = 0;

  let processed = rawText;

  // 1. Mask Email Addresses
  if (EMAIL_REGEX.test(processed)) {
    detectedTypes.add('EMAIL_ADDRESS');
    processed = processed.replace(EMAIL_REGEX, () => {
      count++;
      return '[CONTACT_INFO_MASKED_EMAIL]';
    });
  }

  // 2. Mask Phone Numbers
  if (PHONE_REGEX.test(processed)) {
    detectedTypes.add('PHONE_NUMBER');
    processed = processed.replace(PHONE_REGEX, () => {
      count++;
      return '[CONTACT_INFO_MASKED_PHONE]';
    });
  }

  // 3. Mask UPI Handles
  if (UPI_REGEX.test(processed)) {
    detectedTypes.add('UPI_PAYMENT_HANDLE');
    processed = processed.replace(UPI_REGEX, () => {
      count++;
      return '[PAYMENT_INFO_MASKED_UPI]';
    });
  }

  // 4. Mask External URLs
  if (URL_REGEX.test(processed)) {
    detectedTypes.add('EXTERNAL_URL');
    processed = processed.replace(URL_REGEX, () => {
      count++;
      return '[EXTERNAL_LINK_MASKED]';
    });
  }

  return {
    maskedText: processed,
    detectedTypes: Array.from(detectedTypes),
    detectionsCount: count
  };
}
